import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Select, Spin } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import { useClientSearch } from "../../hooks/useClientSearch";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
} from "../../slices/appStateSlice";
import { resetClientForm, selectRecentClients, setIsEditing } from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import type { TClientForm } from "../../types/index.type";
import styles from "./home.module.scss";

const ClientSelector = () => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobileView();

  const { rootPath } = useRootPath();

  const { referIndividualBy } = useSpecialityReference();

  const recent = useAppSelector(selectRecentClients);
  const { isDemoPath } = useAppSelector((state) => state.appState);
  const { query, results, loading, error, belowMinChars, onSearch, retry } = useClientSearch("all");

  const sourceClients: TClientForm[] = query.trim() ? results : recent;

  const clientOptions = useMemo(() => {
    return sourceClients.map((client) => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

      const secondaryDetails: string[] = [];
      if (client.mrn) {
        secondaryDetails.push(`MRN: ${client.mrn}`);
      }
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }

      return {
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Flex align="center" gap={5}>
              <span>
                {client.name}{" "}
                {!client.is_owned && client.owner_name && (
                  <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                    - Shared by {client.owner_name}
                  </span>
                )}{" "}
              </span>{" "}
            </Flex>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                {secondaryDetails.join(" · ")}
              </span>
            )}
          </div>
        ),
        value: client.id,
        client,
      };
    });
  }, [sourceClients]);

  const selectClient = useCallback(
    (clientId: number) => {
      // Resolve uuid from the chosen option (no in-memory list lookup, so off-page clients work).
      const client = clientOptions.find((o) => o.value === clientId)?.client;
      if (client?.uuid) navigate(`${rootPath}/clients/${client.uuid}`);
    },
    [clientOptions, navigate, rootPath],
  );

  const clearSelection = () => {
    if (!isDemoPath) {
      dispatch(setLoadingNotesState(true));
    }
    navigate(rootPath);
  };

  const showClientView = () => {
    const filterClient = document.querySelector(".filterClient");
    if (filterClient) {
      filterClient.classList.add("ant-select-dropdown-hidden");
    }
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const emptyMessage = belowMinChars
    ? "Type 2+ letters to search by name…"
    : query.trim()
      ? `No ${referIndividualBy.toLowerCase()}s match "${query.trim()}"`
      : recent.length > 0
        ? null
        : `Type a name, DOB, or MRN to find a ${referIndividualBy.toLowerCase()}`;

  return (
    <Flex gap={10}>
      <Select
        showSearch
        placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
        onChange={selectClient}
        onClear={clearSelection}
        allowClear
        listHeight={isMobile ? 66 : 256}
        placement="bottomLeft"
        filterOption={false}
        onSearch={onSearch}
        loading={loading}
        notFoundContent={
          loading ? (
            <div style={{ textAlign: "center", padding: 8 }} aria-busy="true">
              <Spin size="small" />
            </div>
          ) : error ? (
            <div style={{ padding: 8, color: "#cf1322" }}>
              ⚠ Couldn't search — check your connection.{" "}
              <Button type="link" size="small" style={{ padding: 0 }} onClick={retry}>
                Retry
              </Button>
            </div>
          ) : (
            <div style={{ padding: 8, color: "#8c8c8c" }}>{emptyMessage}</div>
          )
        }
        popupClassName="filterClient"
        dropdownRender={(menu) => (
          <div className="client">
            {menu}
            <Divider style={{ margin: "4px 0" }} />
            <Flex gap={10}>
              <Button type="text" icon={<PlusOutlined />} onClick={showClientView}>
                Add {referIndividualBy}
              </Button>
            </Flex>
          </div>
        )}
        style={{ flexGrow: 1 }}
        options={clientOptions}
      />
      <Button
        className={styles[`${componentName}__container-mobile-button`]}
        type="primary"
        onClick={showClientView}
        icon={<PlusOutlined />}
      >
        Add {referIndividualBy}
      </Button>
    </Flex>
  );
};

export default ClientSelector;
